import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useGaSearch, usePurchaseNotes } from "@/api/dashboard";
import { useAddPurchaseItems } from "@/api/purchase";
import { bestellungAlsCsv, csvHerunterladen } from "./csv";

/**
 * Positionen zu einer BESTEHENDEN Bestellung hinzufuegen (Modul 38 / A3-Rueckbau).
 *
 * Warum es diesen Baustein gibt — und warum er VOR dem Loeschen der Entwurfsseite entstand:
 * Der Anlage-Assistent legt NEUE Bestellungen an. Positionen zu einer bereits bestehenden
 * konnte bisher ausschliesslich die Seite `/einkauf/:id/entwurf`. Haette ich sie nach
 * Auftrag einfach entfernt, waere nicht eine Seite verschwunden, sondern eine FAEHIGKEIT:
 * eine nacherfasste Bestellung, der eine Position fehlt, waere in der Oberflaeche nicht mehr
 * zu vervollstaendigen gewesen. Deshalb erst der Ersatz, dann der Rueckbau.
 *
 * Der Weg ist derselbe wie zuvor (`POST /purchase/orders/:id/items/`), nur jetzt dort, wo die
 * Bestellung ohnehin offen ist — statt auf einer zweiten Seite mit eigenem Zustand.
 */
interface Zeile {
  stock_item_id: number;
  merchant_sku: string | null;
  name: string;
  qty: number;
  notiz: string;
}

export function PositionenHinzufuegenDialog({
  orderId,
  offen,
  onClose,
}: {
  orderId: number;
  offen: boolean;
  onClose: () => void;
}) {
  const [suche, setSuche] = useState("");
  const [zeilen, setZeilen] = useState<Zeile[]>([]);
  const { data: suchErgebnis } = useGaSearch(suche);
  const { data: notesData } = usePurchaseNotes("open");
  const addItems = useAddPurchaseItems(orderId);
  const offeneVormerkungen = notesData?.notes ?? [];

  function hinzu(z: Zeile) {
    setZeilen((alt) => {
      if (alt.some((a) => a.stock_item_id === z.stock_item_id)) {
        toast.info(`${z.merchant_sku ?? z.name} steht schon in der Liste`);
        return alt;
      }
      return [...alt, z];
    });
  }

  function schliessen() {
    setZeilen([]);
    setSuche("");
    onClose();
  }

  // Eine Position ohne Menge waere eine Zeile ohne Aussage — das Backend lehnt sie ab, also
  // fragt die Oberflaeche gar nicht erst danach.
  const darfSpeichern =
    zeilen.length > 0 && zeilen.every((z) => Number.isSafeInteger(z.qty) && z.qty > 0);

  function speichern() {
    if (!darfSpeichern) return;
    addItems.mutate(
      zeilen.map((z) => ({
        stock_item_id: z.stock_item_id,
        qty: z.qty,
        anforderungs_notiz: z.notiz.trim() === "" ? null : z.notiz.trim(),
      })),
      {
        onSuccess: () => {
          toast.success(`${zeilen.length} ${zeilen.length === 1 ? "Position" : "Positionen"} hinzugefügt`);
          schliessen();
        },
        onError: () => toast.error("Positionen konnten nicht hinzugefügt werden"),
      },
    );
  }

  return (
    <Dialog open={offen} onOpenChange={(o) => { if (!o) schliessen(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Positionen hinzufügen</DialogTitle>
          <DialogDescription>Artikelsuche oder offene Vormerkungen übernehmen</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <label htmlFor="pos-suche" className="text-xs font-medium text-ink">Artikel suchen</label>
            <Input
              id="pos-suche"
              placeholder="Sternnummer, Lagernummer oder Name"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
            />
            {(suchErgebnis?.items ?? []).length > 0 && (
              <div className="max-h-40 overflow-y-auto rounded border border-line">
                {(suchErgebnis?.items ?? []).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className="flex w-full items-center justify-between px-2 py-1.5 text-left text-xs hover:bg-panel-2"
                    onClick={() => hinzu({ stock_item_id: t.id, merchant_sku: t.merchant_sku ?? null, name: t.name, qty: 1, notiz: "" })}
                  >
                    <span>{t.merchant_sku ?? "—"} · {t.name}</span>
                    <Plus className="size-3.5" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {offeneVormerkungen.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs font-medium text-ink">Offene Vormerkungen ({offeneVormerkungen.length})</div>
              {/* Der Endpunkt fuer Positionen einer BESTEHENDEN Bestellung kennt keine
                  Vormerkungs-Ids — die Vormerkung bleibt also offen. Das war vor dem
                  Rueckbau genauso (Ist-Befund im Aenderungskatalog: „die uebernommene
                  Vormerkung bleibt OFFEN"), aber unsichtbar. Abloesen kann sie nur der
                  Anlage-Assistent ueber den Batch-Weg. Solange das so ist, sagt es die
                  Oberflaeche, statt den Nutzer im Glauben zu lassen, es sei erledigt.
                  (Befund von Reviewer 2, angenommen — als Hinweis, nicht als Fix.) */}
              <p className="text-[11px] text-amber-700 dark:text-amber-400">
                Übernehmen legt die Position an — die Vormerkung bleibt offen und muss separat
                erledigt werden.
              </p>
              <div className="max-h-32 overflow-y-auto rounded border border-line">
                {offeneVormerkungen.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    className="flex w-full items-center justify-between px-2 py-1.5 text-left text-xs hover:bg-panel-2"
                    onClick={() => hinzu({ stock_item_id: n.stock_item_id, merchant_sku: n.merchant_sku ?? null, name: n.name, qty: n.quantity ?? 1, notiz: "" })}
                  >
                    <span>{n.merchant_sku ?? "—"} · {n.name} · {n.quantity ?? "?"}</span>
                    <Plus className="size-3.5" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {zeilen.length === 0 ? (
            <div className="py-4 text-center text-sm text-ink-2">
              Noch nichts gewählt — über die Suche oder eine Vormerkung hinzufügen.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs text-ink-2">
                  <th className="py-1 pr-2">Sternnummer</th>
                  <th className="py-1 pr-2">Artikel</th>
                  <th className="py-1 pr-2 text-right">Menge</th>
                  <th className="py-1 pr-2">Anforderung</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {zeilen.map((z) => (
                  <tr key={z.stock_item_id} className="border-b border-line/50">
                    <td className="py-1 pr-2"><GrundartikelLink code={z.merchant_sku} /></td>
                    <td className="py-1 pr-2">{z.name}</td>
                    <td className="py-1 pr-2 text-right">
                      <Input
                        aria-label={`Menge ${z.merchant_sku ?? z.stock_item_id}`}
                        className="h-7 w-24 text-right"
                        type="number"
                        min={1}
                        value={z.qty}
                        onChange={(e) =>
                          setZeilen((alt) => alt.map((a) => (a.stock_item_id === z.stock_item_id ? { ...a, qty: Number(e.target.value) } : a)))
                        }
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <Input
                        aria-label={`Anforderung ${z.merchant_sku ?? z.stock_item_id}`}
                        className="h-7"
                        value={z.notiz}
                        onChange={(e) =>
                          setZeilen((alt) => alt.map((a) => (a.stock_item_id === z.stock_item_id ? { ...a, notiz: e.target.value } : a)))
                        }
                      />
                    </td>
                    <td className="py-1 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Position ${z.merchant_sku ?? z.stock_item_id} entfernen`}
                        onClick={() => setZeilen((alt) => alt.filter((a) => a.stock_item_id !== z.stock_item_id))}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter className="gap-2">
          {/* Die geloeschte Entwurfsseite konnte ihre NOCH NICHT gespeicherte Liste als CSV
              ausgeben. Der Akten-Export kann das nicht — er kennt nur gespeicherte
              Positionen. Damit der Rueckbau wirklich nichts kostet, gibt es den Knopf hier.
              (Befund von Reviewer 1, angenommen.) */}
          <Button
            variant="outline"
            size="sm"
            disabled={zeilen.length === 0}
            onClick={() => {
              const csv = bestellungAlsCsv(
                zeilen.map((z) => ({
                  merchant_sku: z.merchant_sku, sku: null, name_snapshot: z.name,
                  qty: z.qty, unit_price_usd: null,
                  anforderungs_notiz: z.notiz.trim() === "" ? null : z.notiz.trim(),
                })),
                false,
              );
              csvHerunterladen(`bestellung-${orderId}-entwurfsliste.csv`, csv);
            }}
          >
            Liste als CSV
          </Button>
          <Button variant="outline" size="sm" onClick={schliessen}>Abbrechen</Button>
          <Button size="sm" disabled={!darfSpeichern || addItems.isPending} onClick={speichern}>
            {addItems.isPending ? "Wird gespeichert…" : "Positionen hinzufügen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
